import { useEffect } from 'react';
import { notifyIncomingCall } from '../../../../../shared/actions/userActions';
import { sendCallNoticeMessage } from '../../../../../shared/utils/matrix/matrixReadUtils';
import { hasInProgressMatrixCall } from '../../../../../shared/components/matrix/matrixUtils';

const isCallLive = (call) => Boolean(call && !call.callHasEnded?.());

const normalizeEndedReason = (reason) =>
  String(reason ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const isInviteTimeoutEndedReason = (reason) => {
  if (!reason) return false;
  const normalized = normalizeEndedReason(reason);

  if (
    normalized === 'invite_timeout' ||
    normalized === 'invitetimeout' ||
    normalized === 'no_answer' ||
    normalized === 'no_answer_received_in_time'
  ) {
    return true;
  }

  // Fallback for SDK/version-specific values that still represent unanswered timeout.
  return normalized.includes('timeout') && (normalized.includes('invite') || normalized.includes('answer'));
};

/** Unanswered call ended by timeout, caller hang-up, or callee reject (before connect). */
const isMissedCallEndedReason = (reason) => {
  if (!reason) return false;
  if (isInviteTimeoutEndedReason(reason)) return true;
  return normalizeEndedReason(reason) === 'user_hangup';
};

export const useCallLifecycleNotices = ({
  client,
  isInitialized,
  roomId,
  otherCallUserId,
  shouldAutoPlaceCall,
  lastCallEndedReason,
  activeCall,
  incomingCall,
  hasObservedActiveCall,
  callTypeLabel,
  hasConnectedRef,
  hadLiveCallRef,
  missedCallNoticeSentRef,
  callEndedNoticeSentRef,
  durationAccumulatedSecRef,
  durationStartAtRef,
  formatDuration,
}) => {
  useEffect(() => {
    const sendMissedCallNotice = async () => {
      if (!client || !isInitialized || !roomId) return;
      if (!shouldAutoPlaceCall) return;
      if (!hasObservedActiveCall) return;
      if (missedCallNoticeSentRef.current) return;
      if (!isMissedCallEndedReason(lastCallEndedReason)) return;
      if (hasConnectedRef.current || hadLiveCallRef.current) return;
      if (!otherCallUserId) return;
      // Never post missed-call while any call is still ringing or in progress.
      if (isCallLive(activeCall) || isCallLive(incomingCall)) return;
      if (hasInProgressMatrixCall(client)) return;

      missedCallNoticeSentRef.current = true;
      const missedBody =
        callTypeLabel === 'video' ? 'Video Call' : 'Voice Call';
      try {
        await notifyIncomingCall({
          otherCallUserId,
          callNotificationType: 'missed',
          callType: callTypeLabel === 'video' ? 'video' : 'audio',
        });
      } catch (_) {
        // Keep lifecycle UI stable even when backend notify fails.
      }
      const sent = await sendCallNoticeMessage(client, {
        isInitialized,
        roomId,
        bodyText: missedBody,
      });
      if (!sent) {
        // Allow one retry only if previous send failed.
        missedCallNoticeSentRef.current = false;
      }
    };

    sendMissedCallNotice();
  }, [
    client,
    isInitialized,
    roomId,
    shouldAutoPlaceCall,
    hasObservedActiveCall,
    lastCallEndedReason,
    activeCall,
    incomingCall,
    otherCallUserId,
    callTypeLabel,
    hasConnectedRef,
    hadLiveCallRef,
    missedCallNoticeSentRef,
  ]);

  useEffect(() => {
    if (!lastCallEndedReason) return;
    // Only the caller (outgoing flow) posts the ended-call notice to avoid
    // duplicate timeline messages from both participants.
    if (!shouldAutoPlaceCall) return;
    if (callEndedNoticeSentRef.current) return;
    if (!hasConnectedRef.current && !hadLiveCallRef.current) return;

    const accumulatedSec = durationAccumulatedSecRef.current || 0;
    const startedAt = durationStartAtRef.current;
    const runningExtraSec =
      typeof startedAt === 'number' ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0;
    const durationSeconds = Math.max(0, accumulatedSec + runningExtraSec);

    callEndedNoticeSentRef.current = true;
    sendCallNoticeMessage(client, {
      isInitialized,
      roomId,
      bodyText: `Ended ${callTypeLabel} call - duration ${formatDuration(durationSeconds)}`,
    });
  }, [
    lastCallEndedReason,
    callTypeLabel,
    client,
    isInitialized,
    roomId,
    shouldAutoPlaceCall,
    hasConnectedRef,
    hadLiveCallRef,
    callEndedNoticeSentRef,
    durationAccumulatedSecRef,
    durationStartAtRef,
    formatDuration,
  ]);
};
